import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Search, Dumbbell, Filter as FilterIcon, Bookmark, BookmarkCheck, Tag as TagIcon, Trash2, HeartPulse, Accessibility, BookOpen, FileText, ChevronRight, Lock, PersonStanding } from 'lucide-react';
import { exercises, isBaselineExercise, BENEFICIAL_TAGS } from '../data/exercises';
import { useProfile } from '../context/ProfileContext';
import { useTierState } from '../hooks/useTierState';
import { useFeatureFlag } from '../context/FeatureFlagsContext';
import GearVault from '../components/GearVault';
import TagEditModal from '../components/TagEditModal';
import PaywallModal from '../components/PaywallModal';
import AnatomyDiagram, { regionOf } from '../components/AnatomyDiagram';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Adaptive Lens — tags that count as "adaptive-relevant" when the lens is on.
const ADAPTIVE_LENS_TAGS = ['adaptive', 'iron-limb', 'rehab', 'mobility', 'prosthesis-off', 'wheelchair', 'seated', 'one-arm'];
// CI-10a: the Mobility pill surfaces rehab + all beneficial ("joint-friendly")
// movement — the affirmative, non-clinical "recommend good movement" half.
const MOBILITY_PILL_TAGS = ['rehab', ...BENEFICIAL_TAGS];

const MUSCLES = ['All', 'Chest', 'Back', 'Shoulders', 'Arms', 'Quads', 'Hamstrings', 'Glutes', 'Calves', 'Core', 'Forearms', 'Full Body', 'Palms', 'Wrists', 'Ankles', 'Neck', 'Hip Flexors', 'Adductors', 'Breath/Diaphragm'];
const LEVELS = ['All', 'Beginner', 'Intermediate', 'Advanced'];
const TIERS = ['All', 'Tier 1', 'Tier 2', 'Tier 3'];

const Library = () => {
  const { profile } = useProfile();
  const { is_paid: isPaid, loading: tierLoading } = useTierState();
  const { enabled: phantomLimbEnabled } = useFeatureFlag('phantom_limb_tracker');
  const { enabled: clinicalExercisesEnabled } = useFeatureFlag('clinical_exercises');
  const isAdaptiveAmputee = profile.adaptiveMode && (profile.adaptiveSubmodes || []).includes('amputee');
  const [paywallOpen, setPaywallOpen] = useState(false);

  const ss = (key, fallback) => {
    try { const v = sessionStorage.getItem(key); return v !== null ? v : fallback; } catch { return fallback; }
  };
  const [search, setSearch] = useState(() => ss('enferno_lib_search', ''));
  const [muscle, setMuscle] = useState(() => ss('enferno_lib_muscle', 'All'));
  const [level, setLevel] = useState(() => ss('enferno_lib_level', 'All'));
  const [tier, setTier] = useState(() => ss('enferno_lib_tier', 'All'));
  const [filterByEquipment, setFilterByEquipment] = useState(() => ss('enferno_lib_equip', '0') === '1');
  const [yourSavedOnly, setYourSavedOnly] = useState(() => ss('enferno_lib_saved', '0') === '1');
  useEffect(() => { try { sessionStorage.setItem('enferno_lib_search', search); } catch { /* ignore */ } }, [search]);
  useEffect(() => { try { sessionStorage.setItem('enferno_lib_muscle', muscle); } catch { /* ignore */ } }, [muscle]);
  useEffect(() => { try { sessionStorage.setItem('enferno_lib_level', level); } catch { /* ignore */ } }, [level]);
  useEffect(() => { try { sessionStorage.setItem('enferno_lib_tier', tier); } catch { /* ignore */ } }, [tier]);
  useEffect(() => { try { sessionStorage.setItem('enferno_lib_equip', filterByEquipment ? '1' : '0'); } catch { /* ignore */ } }, [filterByEquipment]);
  useEffect(() => { try { sessionStorage.setItem('enferno_lib_saved', yourSavedOnly ? '1' : '0'); } catch { /* ignore */ } }, [yourSavedOnly]);
  const [rehabOnly, setRehabOnly] = useState(() => {
    try { return localStorage.getItem('enferno_library_rehab_only_v1') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('enferno_library_rehab_only_v1', rehabOnly ? '1' : '0'); } catch { /* ignore */ }
  }, [rehabOnly]);

  // Stretching pill — surfaces only movements tagged `stretching` (the dedicated
  // stretch/flexibility inventory, distinct from the broader Mobility/rehab pill).
  const [stretchOnly, setStretchOnly] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('filter') === 'stretching') return true;
      return localStorage.getItem('enferno_library_stretch_only_v1') === '1';
    } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('enferno_library_stretch_only_v1', stretchOnly ? '1' : '0'); } catch { /* ignore */ }
  }, [stretchOnly]);

  // Adaptive Lens — default ON for adaptive amputee users, OFF otherwise.
  // Persisted to localStorage so the user's choice survives reloads.
  // Honors URL ?lens=adaptive deeplink (e.g., from Profile Learn subsection).
  const [adaptiveLens, setAdaptiveLens] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('lens') === 'adaptive') return true;
      const stored = localStorage.getItem('enferno_library_adaptive_lens_v1');
      if (stored !== null) return stored === '1';
    } catch { /* ignore */ }
    return false;
  });
  useEffect(() => {
    try { localStorage.setItem('enferno_library_adaptive_lens_v1', adaptiveLens ? '1' : '0'); } catch { /* ignore */ }
  }, [adaptiveLens]);
  // Auto-enable lens if user just gained amputee submode and has never toggled it.
  useEffect(() => {
    if (!isAdaptiveAmputee) return;
    try {
      if (localStorage.getItem('enferno_library_adaptive_lens_v1') === null) {
        setAdaptiveLens(true);
      }
    } catch { /* ignore */ }
  }, [isAdaptiveAmputee]);
  const [activeTag, setActiveTag] = useState(null);
  const [customExercises, setCustomExercises] = useState([]);
  const [savedRefs, setSavedRefs] = useState([]); // [{id, library_exercise_id, tags, ...}]
  const [tagEditTarget, setTagEditTarget] = useState(null); // { kind: 'ref'|'custom', record, currentTags }

  // Fetch user's custom exercises + saved library refs. Silent fail on 401 —
  // anonymous users see an empty "Your Saved" view.
  const reload = useCallback(() => {
    axios.get(`${API}/custom-exercises`)
      .then((r) => setCustomExercises(r.data || []))
      .catch(() => { /* silent — empty saved list is the fallback */ });
    axios.get(`${API}/saved-library-refs`)
      .then((r) => setSavedRefs(r.data || []))
      .catch(() => { /* silent */ });
  }, []);
  useEffect(() => { reload(); }, [reload]);

  // Fast lookup: which library exercise IDs has the user already bookmarked?
  const savedRefByLibId = useMemo(() => {
    const map = {};
    savedRefs.forEach((r) => { map[r.library_exercise_id] = r; });
    return map;
  }, [savedRefs]);

  // Tags the user has used across BOTH customs AND saved library refs.
  const userTags = useMemo(() => {
    const all = new Set();
    customExercises.forEach((ex) => (ex.tags || []).forEach((t) => all.add(t)));
    savedRefs.forEach((r) => (r.tags || []).forEach((t) => all.add(t)));
    return Array.from(all).sort();
  }, [customExercises, savedRefs]);

  // Union the customs + resolved library refs into a single "Your Saved" list.
  // Each ref is resolved to its full library exercise data via the static
  // exercises array — refs to deprecated/deleted library exercises are filtered out.
  const savedList = useMemo(() => {
    if (!yourSavedOnly) return [];
    const refRows = savedRefs.map((r) => {
      const lib = exercises.find((e) => e.id === r.library_exercise_id);
      if (!lib) return null;
      return { kind: 'ref', refId: r.id, exercise: lib, tags: r.tags || [] };
    }).filter(Boolean);
    const customRows = customExercises.map((c) => ({
      kind: 'custom', refId: c.id, exercise: c, tags: c.tags || [],
      suggestedToLibrary: c.suggested_to_library,
    }));
    return [...refRows, ...customRows];
  }, [yourSavedOnly, savedRefs, customExercises]);

  const filteredSaved = useMemo(() => {
    if (!yourSavedOnly) return [];
    return savedList.filter((row) => {
      const ex = row.exercise;
      const s = !search || (ex.name || '').toLowerCase().includes(search.toLowerCase());
      const m = muscle === 'All'
        || ex.muscle === muscle
        || (muscle === 'Full Body' && ex.category === 'full-body')
        || (muscle !== 'Full Body' && (ex.altMuscles || []).includes(muscle));
      const tags = ex.tags || [];
      const rh = !rehabOnly || tags.some((tg) => MOBILITY_PILL_TAGS.includes(tg));
      const st = !stretchOnly || tags.includes('stretching');
      const tg = !activeTag || row.tags.includes(activeTag);
      return s && m && rh && st && tg;
    });
  }, [yourSavedOnly, savedList, search, muscle, rehabOnly, stretchOnly, activeTag]);

  const filtered = useMemo(() => {
    if (yourSavedOnly) return [];
    return exercises.filter((e) => {
      // Gate PT/clinical exercises behind the clinical_exercises feature flag.
      if (e.ptClinical && !clinicalExercisesEnabled) return false;
      const s = !search || e.name.toLowerCase().includes(search.toLowerCase());
      const m = muscle === 'All'
        || e.muscle === muscle
        || (muscle === 'Full Body' && e.category === 'full-body')
        || (muscle !== 'Full Body' && (e.altMuscles || []).includes(muscle));
      const l = level === 'All' || e.difficulty === level;
      const t = tier === 'All' || e.tier === Number(tier.split(' ')[1]);
      const eq = !filterByEquipment || e.equipment.length === 0 || e.equipment.every((id) => (profile.equipment || []).includes(id));
      const tags = e.tags || [];
      // Mobility / Joint-friendly toggle — rehab + beneficial (low-impact,
      // joint-health, flare-safe, mobility). CI-10a affirmative surfacing.
      const rh = !rehabOnly || tags.some((tg) => MOBILITY_PILL_TAGS.includes(tg));
      // Stretching pill — dedicated stretch/flexibility inventory.
      const st = !stretchOnly || tags.includes('stretching');
      // Adaptive Lens — pass only if at least one adaptive-relevant tag present.
      const lens = !adaptiveLens || tags.some((tg) => ADAPTIVE_LENS_TAGS.includes(tg));
      return s && m && l && t && eq && rh && st && lens;
    }).sort((a, b) => a.id - b.id);
  }, [search, muscle, level, tier, filterByEquipment, profile.equipment, yourSavedOnly, rehabOnly, stretchOnly, adaptiveLens, clinicalExercisesEnabled]);

  const totalSavedCount = customExercises.length + savedRefs.length;

  // Toggle save/unsave for a library exercise (kept inline because it needs
  // access to setSavedRefs).
  const toggleSaveLibrary = async (e, libExId) => {
    e.preventDefault();
    e.stopPropagation();
    const existing = savedRefByLibId[libExId];
    try {
      if (existing) {
        await axios.delete(`${API}/saved-library-refs/${existing.id}`);
        setSavedRefs((prev) => prev.filter((r) => r.id !== existing.id));
      } else {
        const res = await axios.post(`${API}/saved-library-refs`, { library_exercise_id: libExId, tags: [] });
        setSavedRefs((prev) => [res.data, ...prev]);
      }
    } catch (err) {
      // Most likely 401 (not signed in). Surface a console hint; UI stays in
      // its prior state so the user can retry after auth.
      if (err?.response?.status === 401) {
        // eslint-disable-next-line no-alert
        alert('Sign in to save library exercises to your account.');
      }
    }
  };

  const removeRef = async (refId) => {
    if (!window.confirm('Remove this exercise from your saved list?')) return;
    try {
      await axios.delete(`${API}/saved-library-refs/${refId}`);
      setSavedRefs((prev) => prev.filter((r) => r.id !== refId));
    } catch { /* noop */ }
  };

  const removeCustom = async (custom) => {
    const msg = custom.suggested_to_library
      ? `Remove "${custom.name}" from your saved list?\n\nNote: this exercise was submitted to the Enferno library and is pending admin review. Removing it from your saved list does NOT withdraw your submission. Only an admin can remove a submitted suggestion.`
      : `Permanently delete "${custom.name}"? This cannot be undone.`;
    if (!window.confirm(msg)) return;
    try {
      await axios.delete(`${API}/custom-exercises/${custom.id}`);
      setCustomExercises((prev) => prev.filter((c) => c.id !== custom.id));
    } catch { /* noop */ }
  };

  const saveTagsForRef = async (refId, newTags) => {
    const res = await axios.patch(`${API}/saved-library-refs/${refId}`, { tags: newTags });
    setSavedRefs((prev) => prev.map((r) => (r.id === refId ? res.data : r)));
  };
  const saveTagsForCustom = async (customId, newTags) => {
    const res = await axios.patch(`${API}/custom-exercises/${customId}`, { tags: newTags });
    setCustomExercises((prev) => prev.map((c) => (c.id === customId ? res.data : c)));
  };

  const diffColor = (d) => ({
    Beginner: 'text-green-400 bg-green-500/10',
    Intermediate: 'text-yellow-400 bg-yellow-500/10',
    Advanced: 'text-enf-role-danger bg-enf-role-danger/10',
  }[d]);

  return (
    <div className="bg-transparent text-enf-text-primary min-h-screen pt-20 pb-16">
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-8">
          <div className="text-enf-accent-fg text-xs uppercase tracking-[0.3em] font-semibold mb-2">The Vault</div>
          <h1 className="font-display text-5xl md:text-7xl tracking-wide leading-none mb-3">Every movement. Every tool.</h1>
          <p className="text-enf-text-secondary max-w-2xl">{exercises.length} exercises with anatomy, video demos, and form cues — plus every gear partner Enferno trusts, all in one place.</p>
        </div>

        <div className="relative mb-5">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-enf-text-muted" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search exercises by name..."
            className="w-full pl-10 pr-4 py-3 bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary placeholder:text-enf-text-muted focus:outline-none focus:border-enf-accent-fg transition-colors" />
        </div>

        <div className="flex flex-wrap gap-2 mb-3">
          {MUSCLES.map((m) => (
            <button key={m} onClick={() => setMuscle(m)}
              className={`px-3.5 py-1.5 text-sm rounded-full border transition-colors ${
                muscle === m ? 'bg-enf-accent border-enf-accent-fg text-enf-accent-contrast' : 'bg-enf-surface-deep border-enf-surface-border text-enf-text-secondary hover:text-enf-text-primary hover:border-enf-surface-border'
              }`}>{m}</button>
          ))}
          {/* Your Saved — surfaces user-created custom exercises in one tap. */}
          <button
            onClick={() => { setYourSavedOnly((v) => !v); setActiveTag(null); }}
            data-testid="library-filter-your-saved"
            title="Browse only the exercises you've saved"
            className={`px-3.5 py-1.5 text-sm rounded-full border transition-colors ${
              yourSavedOnly
                ? 'bg-enf-accent border-enf-accent-fg text-enf-accent-contrast'
                : 'bg-enf-surface-deep border-enf-surface-border text-enf-text-secondary hover:text-enf-text-primary hover:border-enf-surface-border'
            }`}
          >
            Your Saved{totalSavedCount > 0 ? ` (${totalSavedCount})` : ''}
          </button>
          {/* Mobility / Joint-friendly — surfaces rehab / chronic-pain / mobility /
              flare-safe + low-impact + joint-health movement in one tap (CI-10a). */}
          <button
            onClick={() => { setRehabOnly((v) => !v); setYourSavedOnly(false); setActiveTag(null); }}
            data-testid="library-filter-rehab"
            title="Show mobility, rehab, low-impact, joint-friendly and flare-safe movements"
            className={`px-3.5 py-1.5 text-sm rounded-full border transition-colors inline-flex items-center gap-1.5 ${
              rehabOnly
                ? 'bg-emerald-700 border-emerald-700 text-enf-text-primary'
                : 'bg-enf-surface-deep border-enf-surface-border text-enf-text-secondary hover:text-enf-text-primary hover:border-enf-surface-border'
            }`}
          >
            <HeartPulse className="w-3.5 h-3.5" />
            Mobility / Joint-friendly
          </button>
          {/* Stretching — surfaces the dedicated stretch/flexibility inventory
              (tag: 'stretching'), distinct from the broader Mobility pill. */}
          <button
            onClick={() => { setStretchOnly((v) => !v); setYourSavedOnly(false); setActiveTag(null); }}
            data-testid="library-filter-stretching"
            title="Show stretching and flexibility movements"
            className={`px-3.5 py-1.5 text-sm rounded-full border transition-colors inline-flex items-center gap-1.5 ${
              stretchOnly
                ? 'bg-emerald-700 border-emerald-700 text-enf-text-primary'
                : 'bg-enf-surface-deep border-enf-surface-border text-enf-text-secondary hover:text-enf-text-primary hover:border-enf-surface-border'
            }`}
          >
            <PersonStanding className="w-3.5 h-3.5" />
            Stretching
          </button>
          {/* Adaptive Lens — when on, library reshapes to surface adaptive
              users' exercises + adaptive learning + gear. Default ON for
              users in Adaptive Mode with Amputee submode. */}
          <button
            onClick={() => setAdaptiveLens((v) => !v)}
            data-testid="library-adaptive-lens-toggle"
            title="Reshape the library for adaptive users — exercises, learning, gear"
            className={`px-3.5 py-1.5 text-sm rounded-full border transition-colors inline-flex items-center gap-1.5 ${
              adaptiveLens
                ? 'bg-adaptive-accent border-adaptive-accent text-enf-text-primary'
                : 'bg-enf-surface-deep border-enf-surface-border text-enf-text-secondary hover:text-enf-text-primary hover:border-enf-surface-border'
            }`}
          >
            <Accessibility className="w-3.5 h-3.5" />
            Adaptive lens
          </button>
        </div>

        {/* Tag sub-filter — only visible when Your Saved is active AND user has at least one tag */}
        {yourSavedOnly && userTags.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid="library-tag-subfilter">
            <span className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mr-1">Your tags:</span>
            {userTags.map((t) => (
              <button
                key={t}
                onClick={() => setActiveTag((cur) => (cur === t ? null : t))}
                className={`px-2.5 py-0.5 text-[11px] rounded-full border transition-colors ${
                  activeTag === t
                    ? 'bg-enf-role-info border-enf-role-info text-enf-accent-contrast'
                    : 'bg-transparent border-enf-surface-border text-enf-text-secondary hover:text-enf-text-primary hover:border-enf-surface-border'
                }`}
                data-testid={`library-tag-${t}`}
              >
                #{t}
              </button>
            ))}
            {activeTag && (
              <button
                onClick={() => setActiveTag(null)}
                className="px-2.5 py-0.5 text-[11px] rounded-full bg-enf-surface-mid border border-enf-surface-border text-enf-text-muted hover:text-enf-text-primary"
              >
                Clear
              </button>
            )}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 mb-3">
          {LEVELS.map((l) => (
            <button key={l} onClick={() => setLevel(l)}
              disabled={yourSavedOnly}
              className={`px-3 py-1 text-xs rounded-md border transition-colors ${
                yourSavedOnly ? 'opacity-40 cursor-not-allowed' :
                level === l ? 'bg-enf-text-primary border-enf-text-primary text-enf-surface-base' : 'bg-transparent border-enf-surface-border text-enf-text-muted hover:text-enf-text-primary'
              }`}>{l}</button>
          ))}
          <span className="w-px h-4 bg-enf-surface-elevated mx-2" />
          {TIERS.map((t) => (
            <button key={t} onClick={() => setTier(t)}
              disabled={yourSavedOnly}
              className={`px-3 py-1 text-xs rounded-md border transition-colors ${
                yourSavedOnly ? 'opacity-40 cursor-not-allowed' :
                tier === t ? 'bg-enf-accent border-enf-accent-fg text-enf-accent-contrast' : 'bg-transparent border-enf-surface-border text-enf-text-muted hover:text-enf-text-primary'
              }`}>{t}</button>
          ))}
        </div>
        {profile.completedOnboarding && (
          <button onClick={() => setFilterByEquipment((v) => !v)}
            className={`mb-8 inline-flex items-center gap-2 px-3 py-1.5 text-xs rounded-md border transition-colors ${
              filterByEquipment ? 'bg-enf-accent/10 border-enf-accent-fg text-enf-accent-fg' : 'bg-transparent border-enf-surface-border text-enf-text-muted hover:text-enf-text-primary'
            }`}>
            <FilterIcon className="w-3 h-3" /> {filterByEquipment ? 'Showing only your equipment' : 'Filter by my equipment'}
          </button>
        )}
        {!profile.completedOnboarding && <div className="mb-8" />}

        {/* Adaptive Lens banner — surfaces Phantom-limb learning, gear,
            and residual-limb log when the lens is on. */}
        {adaptiveLens && (
          <div className="grid sm:grid-cols-3 gap-3 mb-8" data-testid="adaptive-lens-banners">
            {phantomLimbEnabled && (
            <Link
              to="/adaptive/phantom-limb"
              data-testid="adaptive-lens-banner-phantom-limb"
              className="bg-adaptive-accent/10 border border-adaptive-accent/30 rounded-lg p-4 hover:border-adaptive-accent/60 transition-colors group"
            >
              <div className="flex items-center gap-2 mb-1">
                <BookOpen className="w-4 h-4 text-adaptive-accent" />
                <span className="text-[10px] uppercase tracking-widest text-adaptive-accent font-bold">Learn</span>
              </div>
              <div className="font-display text-base tracking-wide leading-tight text-enf-text-primary mb-1">Phantom-limb awareness</div>
              <p className="text-xs text-enf-text-secondary">What sensations are, evidence-based management, when to talk to your clinician.</p>
              <div className="text-xs text-adaptive-accent mt-2 flex items-center gap-1 group-hover:gap-2 transition-all">Read <ChevronRight className="w-3 h-3" /></div>
            </Link>
            )}
            <a
              href="#gear-vault"
              onClick={(e) => {
                e.preventDefault();
                const el = document.querySelector('[data-testid="vault-section-adaptive"]');
                if (el) {
                  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  const btn = el.querySelector('button');
                  if (btn && btn.getAttribute('aria-expanded') === 'false') btn.click();
                }
              }}
              data-testid="adaptive-lens-banner-gear"
              className="bg-adaptive-accent/10 border border-adaptive-accent/30 rounded-lg p-4 hover:border-adaptive-accent/60 transition-colors group"
            >
              <div className="flex items-center gap-2 mb-1">
                <Accessibility className="w-4 h-4 text-adaptive-accent" />
                <span className="text-[10px] uppercase tracking-widest text-adaptive-accent font-bold">Gear</span>
              </div>
              <div className="font-display text-base tracking-wide leading-tight text-enf-text-primary mb-1">Adaptive gear catalog</div>
              <p className="text-xs text-enf-text-secondary">Residual-limb accessories, mobility aids, single-arm grip, pain management. Filterable by variant.</p>
              <div className="text-xs text-adaptive-accent mt-2 flex items-center gap-1 group-hover:gap-2 transition-all">Jump down <ChevronRight className="w-3 h-3" /></div>
            </a>
            <Link
              to="/profile#residual-limb"
              data-testid="adaptive-lens-banner-log"
              className="bg-adaptive-accent/10 border border-adaptive-accent/30 rounded-lg p-4 hover:border-adaptive-accent/60 transition-colors group"
            >
              <div className="flex items-center gap-2 mb-1">
                <FileText className="w-4 h-4 text-adaptive-accent" />
                <span className="text-[10px] uppercase tracking-widest text-adaptive-accent font-bold">Log</span>
              </div>
              <div className="font-display text-base tracking-wide leading-tight text-enf-text-primary mb-1">Residual-limb daily log</div>
              <p className="text-xs text-enf-text-secondary">Skin, socket, phantom pain, wear time. Export to PDF for your care team.</p>
              <div className="text-xs text-adaptive-accent mt-2 flex items-center gap-1 group-hover:gap-2 transition-all">Go to Profile <ChevronRight className="w-3 h-3" /></div>
            </Link>
          </div>
        )}

        {yourSavedOnly ? (
          filteredSaved.length === 0 ? (
            <div className="text-center py-20 text-enf-text-muted" data-testid="library-saved-empty">
              <Dumbbell className="w-10 h-10 mx-auto mb-3 opacity-40" />
              {totalSavedCount === 0
                ? 'No saved exercises yet. Tap the bookmark on any library card to save it, or use "Teach the coach" to add your own.'
                : 'No saved exercises match your filters.'}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="library-saved-grid">
              {filteredSaved.map((row) => {
                const e = row.exercise;
                const isCustom = row.kind === 'custom';
                return (
                  <div key={`${row.kind}-${row.refId}`} className="bg-enf-surface-deep border border-enf-surface-border rounded-xl overflow-hidden hover:border-enf-surface-border transition-all flex flex-col">
                    {!isCustom && (
                      <Link to={`/exercise/${e.id}`} className="aspect-[4/3] relative overflow-hidden block bg-enf-surface-deep flex items-center justify-center">
                        <AnatomyDiagram
                          primary={e.primaryMuscles || []}
                          secondary={e.secondaryMuscles || []}
                          size={110}
                          region={regionOf(e.primaryMuscles)}
                          showLabels={false}
                        />
                        <div className="absolute top-3 left-3 enf-library-index-pill px-2 py-0.5 rounded bg-black/70 backdrop-blur text-xs font-mono font-bold">#{String(e.id).padStart(3, '0')}</div>
                      </Link>
                    )}
                    <div className="p-4 flex-1 flex flex-col">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div className="font-bold text-base text-enf-text-primary">{e.name}</div>
                        <span className={`text-[9px] uppercase tracking-widest rounded px-1 py-0.5 flex-shrink-0 border ${
                          isCustom
                            ? 'text-enf-role-info bg-enf-role-info/10 border-enf-role-info/30'
                            : 'text-enf-text-secondary bg-enf-surface-mid border-enf-surface-border'
                        }`}>{isCustom ? 'custom' : 'library'}</span>
                      </div>
                      <div className="text-xs text-enf-text-muted mb-2">{e.muscle || 'Custom'}</div>
                      {isCustom && e.notes && <div className="text-xs text-enf-text-secondary mb-2 line-clamp-2">{e.notes}</div>}
                      {row.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1 mb-2">
                          {row.tags.map((t) => (
                            <span key={t} className="text-[10px] text-enf-text-muted bg-enf-surface-mid rounded px-1.5 py-0.5">#{t}</span>
                          ))}
                        </div>
                      )}
                      <div className="flex items-center gap-1 mt-auto pt-2 border-t border-enf-surface-border">
                        <button
                          onClick={() => setTagEditTarget({ kind: row.kind, refId: row.refId, name: e.name, currentTags: row.tags })}
                          className="flex-1 inline-flex items-center justify-center gap-1.5 px-2 py-1.5 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded text-[11px] font-semibold text-enf-text-secondary hover:text-enf-text-primary transition-colors"
                          data-testid={`saved-edit-tags-${row.kind}-${row.refId}`}
                        >
                          <TagIcon className="w-3 h-3" /> Edit tags
                        </button>
                        <button
                          onClick={() => isCustom ? removeCustom(e) : removeRef(row.refId)}
                          className="inline-flex items-center justify-center px-2 py-1.5 bg-enf-surface-mid hover:bg-enf-accent border border-enf-surface-border hover:border-enf-accent-fg rounded text-[11px] font-semibold text-enf-text-secondary hover:text-enf-accent-contrast transition-colors"
                          title={isCustom ? (row.suggestedToLibrary ? 'Remove from your saved list (suggestion stays in admin queue)' : 'Permanently delete this custom exercise') : 'Remove from your saved list'}
                          data-testid={`saved-remove-${row.kind}-${row.refId}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-enf-text-muted">
            <Dumbbell className="w-10 h-10 mx-auto mb-3 opacity-40" />
            No exercises match your filters.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filtered.map((e) => {
              const isSaved = Boolean(savedRefByLibId[e.id]);
              const baseline = isBaselineExercise(e);
              const locked = !tierLoading && !isPaid && !baseline;
              if (locked) {
                return (
                  <button
                    key={e.id}
                    type="button"
                    onClick={() => setPaywallOpen(true)}
                    className="group bg-enf-surface-deep border border-enf-surface-border rounded-xl overflow-hidden text-left relative hover:border-enf-accent-fg/40 transition-all"
                    data-testid={`library-card-locked-${e.id}`}
                    aria-label={`${e.name} — locked. Subscribe to unlock.`}
                  >
                    <div className="aspect-[4/3] relative overflow-hidden bg-enf-surface-deep flex items-center justify-center">
                      <div className="opacity-25">
                        <AnatomyDiagram
                          primary={e.primaryMuscles || []}
                          secondary={e.secondaryMuscles || []}
                          size={110}
                          region={regionOf(e.primaryMuscles)}
                          showLabels={false}
                        />
                      </div>
                      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px] flex flex-col items-center justify-center gap-2 group-hover:bg-black/30 transition-colors">
                        <div className="w-10 h-10 rounded-full bg-enf-accent/20 border border-enf-accent-fg/40 flex items-center justify-center">
                          <Lock className="w-4 h-4 text-enf-accent-fg" />
                        </div>
                        <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-enf-accent-fg">Enferno</span>
                      </div>
                      <div className="absolute top-3 left-3 enf-library-index-pill px-2 py-0.5 rounded bg-black/70 backdrop-blur text-xs font-mono font-bold opacity-60">#{String(e.id).padStart(3, '0')}</div>
                    </div>
                    <div className="p-4">
                      <div className="font-bold text-base mb-1 text-enf-text-secondary">{e.name}</div>
                      <div className="flex items-center justify-between text-xs text-enf-text-muted">
                        <span>{e.muscle}</span>
                        <span className="enf-library-tier-label opacity-60" data-tier={e.tier}>Tier {e.tier}</span>
                      </div>
                    </div>
                  </button>
                );
              }
              return (
                <Link key={e.id} to={`/exercise/${e.id}`} className="group bg-enf-surface-deep border border-enf-surface-border rounded-xl overflow-hidden hover:border-enf-surface-border transition-all">
                  <div className="aspect-[4/3] relative overflow-hidden bg-enf-surface-deep flex items-center justify-center">
                    <AnatomyDiagram
                      primary={e.primaryMuscles || []}
                      secondary={e.secondaryMuscles || []}
                      size={110}
                      region={regionOf(e.primaryMuscles)}
                      showLabels={false}
                    />
                    <div className="absolute top-3 left-3 enf-library-index-pill px-2 py-0.5 rounded bg-black/70 backdrop-blur text-xs font-mono font-bold">#{String(e.id).padStart(3, '0')}</div>
                    {e.combatives && (
                      <div className="absolute top-3 left-12 px-1.5 py-0.5 rounded bg-enf-role-warning/90 backdrop-blur text-[10px] font-bold tracking-wider" title="Combatives benefit">🔥</div>
                    )}
                    <button
                      onClick={(ev) => toggleSaveLibrary(ev, e.id)}
                      className={`absolute top-2 right-2 w-8 h-8 rounded-md flex items-center justify-center transition-colors backdrop-blur ${
                        isSaved
                          ? 'bg-enf-accent/90 text-enf-accent-contrast hover:bg-enf-accent'
                          : 'enf-library-bookmark-btn bg-black/60 text-enf-text-secondary hover:bg-black/85 hover:text-enf-text-primary'
                      }`}
                      title={isSaved ? 'Remove from your saved list' : 'Save to your list'}
                      data-testid={`library-bookmark-${e.id}`}
                    >
                      {isSaved ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
                    </button>
                    <div className={`absolute bottom-2 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded text-[10px] font-semibold ${diffColor(e.difficulty)}`}>{e.difficulty}</div>
                  </div>
                  <div className="p-4">
                    <div className="font-bold text-base mb-1 group-hover:text-enf-accent-fg transition-colors">{e.name}</div>
                    <div className="flex items-center justify-between text-xs text-enf-text-muted">
                      <span>{e.muscle}</span>
                      <span className="enf-library-tier-label text-enf-accent-fg/80" data-tier={e.tier}>Tier {e.tier}</span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {/* Tag-edit modal — driven by tagEditTarget state. Same modal handles
            both library refs and custom exercises; the onSave handler routes
            to the right endpoint via tagEditTarget.kind. */}
        <TagEditModal
          open={!!tagEditTarget}
          exerciseName={tagEditTarget?.name || ''}
          currentTags={tagEditTarget?.currentTags || []}
          onSave={async (newTags) => {
            if (!tagEditTarget) return;
            if (tagEditTarget.kind === 'ref') await saveTagsForRef(tagEditTarget.refId, newTags);
            else await saveTagsForCustom(tagEditTarget.refId, newTags);
          }}
          onClose={() => setTagEditTarget(null)}
        />

        <PaywallModal open={paywallOpen} onOpenChange={setPaywallOpen} feature="library_full" />

        {/* The Vault — every gear / supplement partner Enferno surfaces, in one place. */}
        <GearVault />
      </div>
    </div>
  );
};

export default Library;
